import type { SyntheticEvent } from 'react';
import queryString from 'query-string';
import { ChromeEvent } from '@amzn/aac-chrome-api';

// Synchronously readable feature flags, emitted inline by the server. See
// ./stormConfig — usable from consuming applications, including micro-frontends
// rendered inside a shadow root.
export {
  isStormConfigAvailable,
  isStormConfigDarkModeEnabled,
  isStormConfigFeatureEnabled,
} from './stormConfig';

export const limitTextLength = (text: string, maxLength: number): string => {
  return text.length > maxLength ? `${text.substr(0, maxLength - 3)}...` : text;
};

// The currentLocation parameter should be a Location Object (https://developer.mozilla.org/en-US/docs/Web/API/Location)
export const getUrlWithoutParameter = (
  currentLocation: Location,
  param: string,
): string => {
  const urlParams = queryString.parse(window.location.search);
  delete urlParams[param];

  // Object.keys is not supported in IE < 9
  const newUrlParams =
    Object.keys(urlParams).length === 0
      ? ''
      : `?${queryString.stringify(urlParams)}`;

  return `${currentLocation.pathname}${newUrlParams}${currentLocation.hash}`;
};

/*
 * These helpers are handed configs of several unrelated shapes -- breadcrumbs,
 * page-header actions and attributes, section-nav items, pivot-nav items,
 * toasts -- so this describes only what getEventHandler actually touches: the
 * optional per-event callback. The `on${eventType}Callback` index resolves to
 * 'onClickCallback' | 'onCloseCallback' from the eventType union.
 */
type ChromeEventCallback = (event: ChromeEvent) => void;

interface ChromeEventElementConfig {
  onClickCallback?: ChromeEventCallback;
  onCloseCallback?: ChromeEventCallback;
}

/*
 * The returned handler is never registered with addEventListener. Every call site
 * either passes it to a React prop -- so it receives a React SyntheticEvent -- or
 * invokes it directly; ToastBanner invokes it with the deprecated global
 * `window.event`, which is `Event | undefined` and is genuinely undefined on the
 * auto-close timeout path.
 *
 * ChromeEvent.originEvent is declared as a DOM Event, so a synthetic event needs a
 * cast. It is stored as-is rather than unwrapped to `.nativeEvent`: nothing in this
 * workspace reads originEvent, and the ChromeEvents are dispatched to targets that
 * consuming applications subscribe to, so changing the stored value could be
 * observable outside this package.
 */
type HandledEvent = SyntheticEvent | Event | undefined;

function getEventHandler(
  elementConfig: ChromeEventElementConfig,
  eventId: string | undefined,
  eventTarget: EventTarget | undefined,
  eventType: 'Click' | 'Close',
) {
  return (event: HandledEvent) => {
    /*
     * String() is not a behaviour change: ChromeEvent is an alias of the DOM Event
     * constructor, which stringifies its type argument, so `new Event(undefined)`
     * already produces an event whose `.type` is the string 'undefined'. Making it
     * explicit keeps that documented rather than hidden behind an implicit any.
     * Only breadcrumbs reach it -- see getClickEventHandler.
     */
    const chromeEvent = new ChromeEvent(String(eventId));
    chromeEvent.originEvent = event as Event;
    chromeEvent.targetItemConfig = elementConfig;
    /*
     * Read once into a local so the guard narrows it -- TypeScript does not carry a
     * typeof narrowing across two separate computed-key accesses.
     */
    const callback = elementConfig[`on${eventType}Callback`];
    if (typeof callback !== 'undefined') {
      callback(chromeEvent);
    } else if (typeof eventTarget !== 'undefined') {
      /*
       * Deliberately left as typeof !== 'undefined' rather than a truthiness check.
       * It admits null, which would throw here, but no production caller can pass
       * null: the only one passes AACChromeAPI.getCommonEventTarget(), which is a
       * real EventTarget in a browser and undefined otherwise. ToastBanner's
       * "logs an error when dismissing without an onClose callback" test relies on
       * this throw, forcing null in to prove its catch block logs -- tightening the
       * guard here would silently defeat that test.
       */
      eventTarget.dispatchEvent(chromeEvent);
    }
  };
}

/*
 * `id` is optional because Breadcrumb (src/app/slice) does not declare one at all,
 * and MobileBreadcrumb passes a breadcrumb straight in. The resulting ChromeEvent
 * is inert rather than broken: its type becomes the string 'undefined', and because
 * MobileBreadcrumb also passes no eventTarget it is never dispatched -- it only
 * reaches an onClickCallback if the breadcrumb defines one. Giving Breadcrumb a
 * real id would make breadcrumb clicks subscribable, which is a product decision
 * affecting external consumers, not a typing one.
 */
export const getClickEventHandler = (
  elementConfig: ChromeEventElementConfig & { id?: string },
  eventTarget?: EventTarget,
) => {
  return getEventHandler(elementConfig, elementConfig.id, eventTarget, 'Click');
};

export const getCloseEventHandler = (
  elementConfig: ChromeEventElementConfig & { aacEventId: string },
  eventTarget: EventTarget | undefined,
) => {
  return getEventHandler(
    elementConfig,
    elementConfig.aacEventId,
    eventTarget,
    'Close',
  );
};

// This method checks if the script[type=internal-chrome-context] exists on the page. It is currently printed on the page by BigTopSharedWidgetService.
export const isBtswContext = (): boolean => {
  const initContextTag = document.querySelector(
    'script[type=internal-chrome-context]',
  );
  return initContextTag !== null;
};

export const isFromBtswWc = (): boolean => {
  return !!document.querySelector(
    'meta[name="a2z:btsw_initial_chrome_config"]',
  );
};

export const isFromTrim = (): boolean => {
  return !!document.querySelector('meta[name="a2z:mons_site_name"]');
};

export const shouldLoadSystemJsAssets = (): boolean => {
  return window.System && window.System.import && !isFromTrim();
};
